class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      if (!event.target.classList.contains('custom-filter')) {
        this.onSubmitHandler(event);
      }
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  connectedCallback() {
    const filterClearBtn = document.querySelector('.filter__clear-btn');
    if (!filterClearBtn) return;
    if (this.clearBtnListener) return; // Prevent multiple bindings

    // Store reference for cleanup
    this.clearBtnListener = this.onClearFilterClick.bind(this);
    filterClearBtn.addEventListener('click', this.clearBtnListener);
  }

  disconnectedCallback() {
    const filterClearBtn = document.querySelector('.filter__clear-btn');
    // Clean up the listener reference even if button is not found
    if (this.clearBtnListener) {
      if (filterClearBtn) {
        filterClearBtn.removeEventListener('click', this.clearBtnListener);
      }
      this.clearBtnListener = null;
    }
  }

  onClearFilterClick(event) {
    const filterClearBtn = event.currentTarget;
    if (!filterClearBtn) return;

    if (filterClearBtn.getAttribute('data-state') === 'default') {
      // Change the state to 'loading'
      filterClearBtn.setAttribute('data-state', 'loading');

      // Optionally, update the UI to indicate loading
      const filterClearBtnBody = filterClearBtn.querySelector('.filter__clear-btn-body');
      const loadingSpinner = filterClearBtn.querySelector('.loading__spinner');

      if (filterClearBtnBody && loadingSpinner) {
        filterClearBtnBody.classList.add('filter__clear-btn-body--invisible');
        loadingSpinner.classList.remove('hidden');
      }

      const tagFilters = this.querySelectorAll('input[type="checkbox"].tag-filter');
      if (!tagFilters?.length) return;

      tagFilters.forEach((checkbox) => {
        checkbox.checked = false;
      });

      try {
        this.onSubmitHandler(event);
      } catch (error) {
        console.error('Failed to submit form:', error);
        // Reset button state on error
        FacetFiltersForm.resetButtonState();
      }
    } else {
      // Prevent multiple clicks
      event.preventDefault();
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;

        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.attachFacetRemoveListeners(); // Attach listeners to new elements
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.updateFacetRemoves();

        FacetFiltersForm.resetButtonState();

        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.attachFacetRemoveListeners(); // Attach listeners to new elements
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.updateFacetRemoves();

    FacetFiltersForm.resetButtonState();

    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter, #FacetFiltersPillsDrawerForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter, #FacetFiltersPillsDrawerForm .js-filter'
    );
    // Capture the state of the dropdowns
    const dropdownState = Array.from(facetDetailsElementsFromDom).reduce((state, element) => {
      state[element.id] = element.open;
      return state;
    }, {});

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        currentElement.innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            if (document.getElementById(previousElementId)) {
              document.getElementById(previousElementId).after(elementToRender);
            }
            return;
          }
        }

        if (elementToRender.parentElement) {
          if (document.querySelector(`#${elementToRender.parentElement.id} .js-filter`)) {
            document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
          }
        }
      }
    });

    // Restore the state of the dropdowns
    Object.keys(dropdownState).forEach((id) => {
      const element = document.getElementById(id);
      if (element) {
        element.open = dropdownState[id];
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    FacetFiltersForm.handleAccordeonClick();
    const activeFacetElementSelectors = [
      '.active-facets-mobile',
      '.header-active-facets',
      '.mobile-facets__footer',
      '.mobile-drawer-sorting-filter .value-sorting',
    ];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });
    FacetFiltersForm.toggleActiveFacets(false);
  }
  static removeAllFacetsOfOption(optionName) {
    const urlParams = new URLSearchParams(window.location.search);

    // Remove all filters related to the specified option
    [...urlParams.keys()].forEach((key) => {
      if (key.startsWith(optionName)) {
        urlParams.delete(key);
      }
    });

    // Build new search params string
    const newSearchParams = urlParams.toString();

    // Update the page without reloading and without closing the filter
    FacetFiltersForm.renderPage(newSearchParams, null, false); // Note the `false` here to avoid reloading

    // Update the URL
    FacetFiltersForm.updateURLHash(newSearchParams);
  }
  static initDropdownOption() {
    document.addEventListener('DOMContentLoaded', function () {
      let openDropdown = null;

      function handleDropdownClick(clickedDropdown) {
        if (!clickedDropdown) return;
        const clickedSubmenu = clickedDropdown.querySelector('.mobile-facets__submenu');

        // Close the currently open dropdown if it's different from the one being clicked
        if (openDropdown && openDropdown !== clickedDropdown) {
          clickedDropdown.querySelector('svg').style.transform = 'rotate(0deg)';

          clickedDropdown.querySelector('.mobile-facets__submenu').classList.remove('show');
        }

        // Toggle the 'show' class on the clicked dropdown
        clickedSubmenu.classList.toggle('show');

        if (clickedDropdown.querySelector('.mobile-facets__submenu').classList.contains('show')) {
          clickedDropdown.querySelector('svg').style.transform = 'rotate(180deg)';
        } else {
          clickedDropdown.querySelector('svg').style.transform = 'rotate(0deg)';
        }
        // Update the reference to the currently open dropdown
        openDropdown = clickedSubmenu.classList.contains('show') ? clickedDropdown : null;
      }

      // Event delegation approach
      document.addEventListener('click', function (event) {
        const summary = event.target.closest('.mobile-facets__summary');
        if (summary) {
          const dropdown = summary.closest('.mobile-facets__details');
          handleDropdownClick(dropdown);
        }
      });

      // Re-initialize dropdowns after rendering is complete
      document.addEventListener('shopify:section:load', function () {
        console.log('Section loaded');
      });

      // Listen for the AJAX response to re-initialize dropdowns
      document.addEventListener('shopify:section:select', function () {
        console.log('Section selected');
      });
    });
  }
  static attachFacetRemoveListeners() {
    document.querySelectorAll('.remove-facet-option').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();

        // Get the filter option to remove
        const optionName = button.getAttribute('data-filter');

        // Call the function to remove the facet option
        FacetFiltersForm.removeAllFacetsOfOption(optionName);

        // Ensure that the page doesn't reload or filter closes
        // Additional logic may be added here if needed
      });
    });
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }
  static handleAccordeonClick() {
    let dropDownItemsMenuDrawer = document
      .getElementById('FacetFiltersFormMobile')
      .closest('menu-drawer')
      .querySelectorAll('.mobile-facets__summary.focus-inset');
    dropDownItemsMenuDrawer.forEach((dropdown) => {
      const submenu = dropdown.parentElement.querySelector('.mobile-facets__submenu');
      dropdown.addEventListener('click', () => {
        if (submenu.classList.contains('show')) {
          submenu.parentElement.classList.remove('show');
          dropdown.querySelector('svg').style.transform = 'rotate(0)';
        } else {
          submenu.parentElement.classList.add('show');
          dropdown.querySelector('svg').style.transform = 'rotate(180deg)';
        }
      });
    });
  }
  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const targetElement = event.target;
    const clearBtnElement = event.currentTarget;

    let isClearBtnClicked = clearBtnElement && clearBtnElement.classList.contains('filter__clear-btn');

    // Check if the input has the class 'tag-filter'
    if (targetElement.classList.contains('tag-filter') || isClearBtnClicked) {
      const currentUrl = new URL(window.location.href);
      const pathSegments = currentUrl.pathname.split('/');

      const tagFilters = this.querySelectorAll('input[type="checkbox"].tag-filter');

      // Filter the checked tag filters and get their values
      const checkedTagFilterValues = Array.from(tagFilters)
        .filter((checkbox) => checkbox.checked)
        .map((checkbox) => {
          const value = checkbox.value.trim();
          // Only allow alphanumeric characters including German characters, spaces, comma, ampersand, plus, and hyphens
          if (!/^[\w\s,&+\-äöüÄÖÜß]+$/.test(value)) {
            console.warn(`Invalid tag value: ${value}`);
            return '';
          }
          return value.replace(/\s+/g, '-');
        })
        .filter(Boolean);

      const checkedTagFilterValuesStr = checkedTagFilterValues.join('+');

      const collectionsIndex = pathSegments.findIndex((segment) => segment === 'collections');
      if (collectionsIndex !== -1) {
        const newUrl = this.constructFilterUrl(pathSegments, collectionsIndex, checkedTagFilterValuesStr, currentUrl);

        history.pushState({}, '', newUrl);
      }
    } else if (targetElement.className === 'mobile-facets__checkbox' && !isClearBtnClicked) {
      const searchParams = this.createSearchParams(targetElement.closest('form'));
      this.onSubmitForm(searchParams, event);
      return; // Exit early
    }

    // Continue with form submission logic for other forms
    const forms = [];
    const isMobile = !isClearBtnClicked && targetElement.closest('form')?.id === 'FacetFiltersFormMobile';
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');

    const validFormIds = ['FacetSortForm', 'FacetFiltersForm', 'FacetSortDrawerForm', 'FacetFiltersFormMobile'];
    sortFilterForms.forEach((form) => {
      // Skip forms containing the tag filter
      const hasTagFilter = form.querySelector('.tag-filter');
      const isValidForm = validFormIds.includes(form.id) && (!isMobile || form.id === 'FacetFiltersFormMobile');

      if (!hasTagFilter && isValidForm) {
        forms.push(this.createSearchParams(form));
      }
    });
    this.onSubmitForm(forms.join('&'));
  }

  constructFilterUrl(pathSegments, collectionsIndex, checkedTagFilterValuesStr, currentUrl) {
    const prefix = collectionsIndex > 1 ? `/${pathSegments[1]}` : '';
    const collectionName = pathSegments[collectionsIndex + 1];
    const baseUrl = `${prefix}/collections/${collectionName}/${checkedTagFilterValuesStr}`;

    const params = new URLSearchParams(currentUrl.searchParams);
    params.delete('tag');
    const queryString = params.toString();

    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }

  static updateFacetRemoves() {
    const applidedFiltersCnt = FacetRemove.getAppliedFilters();

    document.querySelectorAll('.active-facets__button-wrapper').forEach((facetRemove) => {
      if (applidedFiltersCnt === 0) {
        facetRemove.classList.add('hidden');
      } else {
        facetRemove.classList.remove('hidden');

        const spanElem = facetRemove.querySelector('span');
        if (spanElem) spanElem.innerHTML = window.facets.clear_n_filters.replace('[count]', applidedFiltersCnt);
      }
    });
  }

  static resetButtonState() {
    const filterClearBtn = document.querySelector('.filter__clear-btn');
    if (filterClearBtn.getAttribute('data-state') === 'default') return;

    // Reset the button state back to default after the operation
    filterClearBtn.setAttribute('data-state', 'default');

    const filterClearBtnBody = filterClearBtn.querySelector('.filter__clear-btn-body');
    const loadingSpinner = filterClearBtn.querySelector('.loading__spinner');

    if (filterClearBtnBody && loadingSpinner) {
      filterClearBtnBody.classList.remove('filter__clear-btn-body--invisible');
      loadingSpinner.classList.add('hidden');
    }
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.handleAccordeonClick();
FacetFiltersForm.attachFacetRemoveListeners();
FacetFiltersForm.initDropdownOption();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  connectedCallback() {
    this.updateFacetRemoveElem();
  }

  updateFacetRemoveElem() {
    const applidedFiltersCnt = FacetRemove.getAppliedFilters();

    if (applidedFiltersCnt === 0) {
      this.classList.add('hidden');
    } else {
      this.classList.remove('hidden');

      const spanElem = this.querySelector('span');
      if (spanElem) spanElem.innerHTML = window.facets.clear_n_filters.replace('[count]', applidedFiltersCnt);
    }
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }

  static getAppliedFilters() {
    const url = new URL(window.location.href);

    const params = new URLSearchParams(url.search);

    let appliedFilters = [];

    params.forEach((value, key) => {
      if (key.indexOf('filter.') >= 0) {
        if (value !== '') {
          if (!appliedFilters[key] || appliedFilters[key].length === 0) appliedFilters[key] = [];

          appliedFilters[key].push(value);
        }
      }
    });

    const applidedFiltersCnt = Object.keys(appliedFilters).filter((key) => appliedFilters[key].length > 0).length;

    return applidedFiltersCnt;
  }
}

customElements.define('facet-remove', FacetRemove);
